import React, { useState } from "react";
import { v4 } from "uuid";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import PaymentIcon from "@mui/icons-material/Payment";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ChecklistIcon from "@mui/icons-material/Checklist";
import SendIcon from "@mui/icons-material/Send";
import StoreIcon from "@mui/icons-material/Store";
import CategoryIcon from "@mui/icons-material/Category";
import NotesIcon from "@mui/icons-material/Notes";
import ReceiptIcon from "@mui/icons-material/Receipt";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  IPendingMovementsResponse,
  IPendingExpenseApproval,
  IPendingWarehouseTransfer,
  IPendingPaymentConfirmation,
  IPendingWithdrawAuthorization,
} from "../../common/interfaces";
import { CommonService } from "../../services/CommonService";

interface PendingMovementsModalProps {
  open: boolean;
  data: IPendingMovementsResponse | null;
  userId: number;
  cashBoxId: string;
  onClose: () => void;
  onRefresh: () => void;
}

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleString("es-MX", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatCurrency = (amount: number): string =>
  new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(amount);

const ResendButton: React.FC<{ sending: boolean; onClick: () => void; label?: string }> = ({
  sending,
  onClick,
  label = "Reenviar al administrador",
}) => (
  <Button
    size="small"
    variant="outlined"
    startIcon={sending ? <CircularProgress size={14} /> : <SendIcon sx={{ fontSize: "16px !important" }} />}
    disabled={sending}
    onClick={onClick}
    sx={{
      fontSize: "0.82rem",
      py: 0.4,
      px: 1.5,
      borderColor: "#1565C0",
      color: "#1565C0",
      "&:hover": { bgcolor: "#E3F2FD", borderColor: "#1565C0" },
      textTransform: "none",
      whiteSpace: "nowrap",
    }}
  >
    {sending ? "Enviando..." : label}
  </Button>
);

const ActionChip: React.FC<{ label: string }> = ({ label }) => (
  <Chip
    icon={<WarningAmberIcon sx={{ fontSize: "16px !important" }} />}
    label={label}
    size="small"
    sx={{
      bgcolor: "#FFF3E0",
      color: "#E65100",
      border: "1px solid #FFCC02",
      fontWeight: 600,
      fontSize: "0.8rem",
      height: 28,
      "& .MuiChip-icon": { color: "#E65100" },
    }}
  />
);

const DateLabel: React.FC<{ date: string }> = ({ date }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
    <AccessTimeIcon sx={{ fontSize: 15, color: "#424242" }} />
    <Typography variant="body2" sx={{ color: "#424242" }}>
      {formatDate(date)}
    </Typography>
  </Box>
);

// Generic item for transfers, payments, withdrawals
const MovementItem: React.FC<{
  message: string;
  action: string;
  date: string;
  onResend?: () => Promise<void>;
}> = ({ message, action, date, onResend }) => {
  const [sending, setSending] = useState(false);

  const handleResend = async () => {
    if (!onResend) return;
    setSending(true);
    try { await onResend(); } finally { setSending(false); }
  };

  return (
    <Box
      sx={{
        mb: 1.5,
        borderRadius: 2,
        border: "1px solid #e0e0e0",
        bgcolor: "white",
        boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
        overflow: "hidden",
      }}
    >
      <Box sx={{ px: 2, py: 1.5 }}>
        <Typography variant="body1" sx={{ color: "#212121", lineHeight: 1.5 }}>
          {message}
        </Typography>
      </Box>
      <Box
        sx={{
          px: 2,
          py: 1.25,
          borderTop: "1px solid #f0f0f0",
          bgcolor: "#fafafa",
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <ActionChip label={action} />
        {onResend && <ResendButton sending={sending} onClick={handleResend} />}
        <Box sx={{ ml: "auto" }}>
          <DateLabel date={date} />
        </Box>
      </Box>
    </Box>
  );
};

// Expense item with compact row + expandable details
const ExpenseMovementItem: React.FC<{
  expense: IPendingExpenseApproval;
  onResend: () => Promise<void>;
}> = ({ expense, onResend }) => {
  const [sending, setSending] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const handleResend = async () => {
    setSending(true);
    try { await onResend(); } finally { setSending(false); }
  };

  return (
    <Box
      sx={{
        mb: 1.5,
        borderRadius: 2,
        border: "1px solid #e0e0e0",
        bgcolor: "white",
        boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
        overflow: "hidden",
      }}
    >
      {/* Compact summary row */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          cursor: "pointer",
          "&:hover": { bgcolor: "#fafafa" },
        }}
        onClick={() => setExpanded((v) => !v)}
      >
        <StoreIcon sx={{ fontSize: 22, color: "#1565C0", flexShrink: 0 }} />
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="body1" sx={{ fontWeight: 700, color: "#212121", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {expense.supplier_name}
          </Typography>
          <Typography variant="body2" sx={{ color: "#424242" }}>
            {expense.concepts}
          </Typography>
        </Box>
        <Box sx={{ flexShrink: 0, textAlign: "right" }}>
          <Typography variant="body1" sx={{ fontWeight: 800, color: "#1565C0" }}>
            {formatCurrency(expense.total)}
          </Typography>
          <Chip
            label={expense.payment_type}
            size="small"
            sx={{ bgcolor: "#E8F5E9", color: "#2E7D32", fontWeight: 600, fontSize: "0.78rem", height: 22, mt: 0.25 }}
          />
        </Box>
        <IconButton
          size="small"
          sx={{
            color: "#424242",
            flexShrink: 0,
            transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s",
          }}
          onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }}
        >
          <ExpandMoreIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Expanded detail panel */}
      <Collapse in={expanded}>
        <Divider />
        <Box sx={{ bgcolor: "#fafafa" }}>
          {/* Concepts */}
          <Box sx={{ px: 2, py: 1.5, display: "flex", alignItems: "flex-start", gap: 1.5 }}>
            <CategoryIcon sx={{ fontSize: 20, color: "#6A1B9A", mt: 0.15, flexShrink: 0 }} />
            <Box>
              <Typography variant="body2" sx={{ color: "#424242", display: "block", fontWeight: 700, mb: 0.25 }}>Conceptos</Typography>
              <Typography variant="body1" sx={{ color: "#212121" }}>{expense.concepts}</Typography>
              {expense.concepts_description && (
                <Typography variant="body2" sx={{ color: "#424242", mt: 0.25 }}>{expense.concepts_description}</Typography>
              )}
            </Box>
          </Box>

          {/* Notes */}
          {expense.notes && (
            <>
              <Divider sx={{ mx: 2 }} />
              <Box sx={{ px: 2, py: 1.5, display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                <NotesIcon sx={{ fontSize: 20, color: "#E65100", mt: 0.15, flexShrink: 0 }} />
                <Box>
                  <Typography variant="body2" sx={{ color: "#424242", display: "block", fontWeight: 700, mb: 0.25 }}>Notas</Typography>
                  <Typography variant="body1" sx={{ color: "#212121" }}>{expense.notes}</Typography>
                </Box>
              </Box>
            </>
          )}

          {/* Totals */}
          <Divider />
          <Box sx={{ px: 2, py: 1.5, display: "flex", gap: 4, flexWrap: "wrap", alignItems: "center" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <ReceiptIcon sx={{ fontSize: 17, color: "#424242" }} />
              <Box>
                <Typography variant="body2" sx={{ color: "#424242", display: "block", lineHeight: 1.2, fontWeight: 700 }}>Subtotal</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, color: "#212121" }}>{formatCurrency(expense.subtotal)}</Typography>
              </Box>
            </Box>
            <Box>
              <Typography variant="body2" sx={{ color: "#424242", display: "block", lineHeight: 1.2, fontWeight: 700 }}>IVA</Typography>
              <Typography variant="body1" sx={{ fontWeight: 600, color: "#212121" }}>{formatCurrency(expense.iva)}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" sx={{ color: "#424242", display: "block", lineHeight: 1.2, fontWeight: 700 }}>Total</Typography>
              <Typography variant="body1" sx={{ fontWeight: 800, color: "#1565C0", fontSize: "1.05rem" }}>{formatCurrency(expense.total)}</Typography>
            </Box>
          </Box>
        </Box>
      </Collapse>

      {/* Footer: always visible */}
      <Box
        sx={{
          px: 2,
          py: 1,
          borderTop: "1px solid #f0f0f0",
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <ActionChip label={expense.action_required} />
        <ResendButton sending={sending} onClick={handleResend} />
        <Box sx={{ ml: "auto" }}>
          <DateLabel date={expense.created_at} />
        </Box>
      </Box>
    </Box>
  );
};

const SectionHeader: React.FC<{
  icon: React.ReactNode;
  label: string;
  count: number;
  color: string;
  bgcolor: string;
}> = ({ icon, label, count, color, bgcolor }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1.5,
      mb: 1.5,
      px: 1.5,
      py: 1.25,
      borderRadius: 1.5,
      bgcolor,
    }}
  >
    <Box sx={{ color, display: "flex" }}>{icon}</Box>
    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#212121", flexGrow: 1 }}>
      {label}
    </Typography>
    <Chip
      label={count}
      size="small"
      sx={{ bgcolor: color, color: "white", fontWeight: 700, minWidth: 32, height: 24, fontSize: "0.82rem" }}
    />
  </Box>
);

export const PendingMovementsModal: React.FC<PendingMovementsModalProps> = ({
  open,
  data,
  userId,
  cashBoxId,
  onClose,
  onRefresh,
}) => {
  if (!data) return null;

  const { summary, pending_movements, instructions } = data;

  const handleResendExpense = async (expense: IPendingExpenseApproval) => {
    const res = await CommonService.requestAuthorize({
      message: `Se solicita Autorizar un Gasto $ ${expense.total.toFixed(2)}`,
      action: {
        type: "authorize-bill",
        item: { bill: expense.id },
        id: v4(),
        content: `Se solicita Autorizar un Gasto.<br/><b>Total:</b> $ ${expense.total.toFixed(2)}<br/><b>Proveedor:</b> ${expense.supplier_name}<br/><b>Observaciones:</b> ${expense.notes}`,
      },
      status: "pending",
      from: userId,
      to: 1,
    });
    if (res.success) {
      CommonService.toast("Notificación reenviada al administrador", "success");
    } else {
      CommonService.toast(res.message || "Error al reenviar la notificación", "error");
    }
  };

  const handleResendWithdraw = async (withdraw: IPendingWithdrawAuthorization) => {
    const res = await CommonService.requestAuthorize({
      message: `Se solicita Aprobar retiro Monto: $ ${withdraw.amount}`,
      action: {
        type: "authorize-withdraw",
        item: { amount: withdraw.amount, user: userId, cashbox: cashBoxId },
        id: v4(),
        content: `Se solicita Aprobar retiro Monto: $ ${withdraw.amount}`,
      },
      status: "pending",
      from: userId,
      to: 1,
    });
    if (res.success) {
      CommonService.toast("Notificación reenviada al administrador", "success");
    } else {
      CommonService.toast(res.message || "Error al reenviar la notificación", "error");
    }
  };

  const handleResendPayment = async (payment: IPendingPaymentConfirmation) => {
    const res = await CommonService.requestAuthorize({
      message: `Se solicita Autorizar un pago por Transferencia de $ ${payment.payment_amount.toFixed(2)}`,
      action: {
        type: "authorize-payment",
        item: { order_id: payment.order_id },
        id: v4(),
        content: `Se solicita Autorizar un pago por Transferencia de $ ${payment.payment_amount.toFixed(2)}<br/><b>Cliente:</b> ${payment.customer_name}`,
      },
      status: "pending",
      from: userId,
      to: 1,
    });
    if (res.success) {
      CommonService.toast("Notificación reenviada al administrador", "success");
    } else {
      CommonService.toast(res.message || "Error al reenviar la notificación", "error");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: "hidden", maxHeight: "90vh" } }}
    >
      {/* Header */}
      <DialogTitle sx={{ p: 0 }}>
        <Box
          sx={{
            bgcolor: "#C62828",
            color: "white",
            px: 3,
            py: 2,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <WarningAmberIcon sx={{ fontSize: 22 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, flexGrow: 1, fontSize: "1.05rem" }}>
            {instructions?.title || "No puedes cerrar la caja aún"}
          </Typography>
          <IconButton size="small" onClick={onClose} sx={{ color: "rgba(255,255,255,0.8)", "&:hover": { color: "white" } }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Box sx={{ bgcolor: "#FFECB3", px: 3, py: 1.25, display: "flex", alignItems: "center", gap: 1 }}>
          <WarningAmberIcon sx={{ fontSize: 16, color: "#F57F17" }} />
          <Typography variant="body2" sx={{ color: "#5D4037", fontWeight: 500 }}>
            {instructions?.description || "Tienes movimientos pendientes que deben completarse antes de cerrar caja"}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ bgcolor: "#f5f5f5", px: 3, py: 2.5, maxHeight: "65vh", overflowY: "auto" }}>
        {/* Summary */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            mb: 2.5,
            p: 1.75,
            borderRadius: 2,
            bgcolor: "white",
            border: "1px solid #e0e0e0",
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              bgcolor: "#C62828",
              color: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 700,
              fontSize: "1rem",
              flexShrink: 0,
            }}
          >
            {summary.total_pending}
          </Box>
          <Typography variant="body1" sx={{ color: "#212121", fontWeight: 500 }}>
            {summary.total_pending === 1
              ? "movimiento pendiente por resolver"
              : "movimientos pendientes por resolver"}
          </Typography>
        </Box>

        {/* Gastos */}
        {summary.by_type.pending_expense_approvals > 0 && (
          <Box sx={{ mb: 2.5 }}>
            <SectionHeader
              icon={<ReceiptLongIcon fontSize="small" />}
              label="Gastos pendientes de aprobación"
              count={summary.by_type.pending_expense_approvals}
              color="#1565C0"
              bgcolor="#E3F2FD"
            />
            {pending_movements.pending_expense_approvals.map((expense: IPendingExpenseApproval) => (
              <ExpenseMovementItem
                key={expense.id}
                expense={expense}
                onResend={() => handleResendExpense(expense)}
              />
            ))}
          </Box>
        )}

        {/* Traspasos */}
        {summary.by_type.pending_warehouse_transfers > 0 && (
          <Box sx={{ mb: 2.5 }}>
            <SectionHeader
              icon={<LocalShippingIcon fontSize="small" />}
              label="Traspasos entre almacenes"
              count={summary.by_type.pending_warehouse_transfers}
              color="#6A1B9A"
              bgcolor="#F3E5F5"
            />
            {pending_movements.pending_warehouse_transfers.map((transfer: IPendingWarehouseTransfer) => (
              <MovementItem
                key={transfer.id}
                message={transfer.user_friendly_message}
                action={transfer.action_required}
                date={transfer.created_at}
              />
            ))}
          </Box>
        )}

        {/* Pagos por transferencia */}
        {summary.by_type.pending_payment_confirmations > 0 && (
          <Box sx={{ mb: 2.5 }}>
            <SectionHeader
              icon={<PaymentIcon fontSize="small" />}
              label="Pagos por transferencia pendientes"
              count={summary.by_type.pending_payment_confirmations}
              color="#2E7D32"
              bgcolor="#E8F5E9"
            />
            {pending_movements.pending_payment_confirmations.map((payment: IPendingPaymentConfirmation) => (
              <MovementItem
                key={payment.id}
                message={payment.user_friendly_message}
                action={payment.action_required}
                date={payment.created_at}
                onResend={() => handleResendPayment(payment)}
              />
            ))}
          </Box>
        )}

        {/* Retiros */}
        {summary.by_type.pending_withdraw_authorizations > 0 && (
          <Box sx={{ mb: 2.5 }}>
            <SectionHeader
              icon={<AccountBalanceIcon fontSize="small" />}
              label="Retiros pendientes de autorización"
              count={summary.by_type.pending_withdraw_authorizations}
              color="#BF360C"
              bgcolor="#FBE9E7"
            />
            {pending_movements.pending_withdraw_authorizations.map((withdraw: IPendingWithdrawAuthorization) => (
              <MovementItem
                key={withdraw.id}
                message={withdraw.user_friendly_message}
                action={withdraw.action_required}
                date={withdraw.requested_at}
                onResend={() => handleResendWithdraw(withdraw)}
              />
            ))}
          </Box>
        )}

        {/* Pasos a seguir */}
        {instructions?.steps && (
          <Box sx={{ p: 2, borderRadius: 2, bgcolor: "white", border: "1px solid #e0e0e0" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.25 }}>
              <ChecklistIcon sx={{ fontSize: 18, color: "#424242" }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#212121" }}>
                Pasos a seguir
              </Typography>
            </Box>
            <Box component="ol" sx={{ pl: 2, m: 0 }}>
              {instructions.steps.map((step: string, index: number) => (
                <Typography
                  key={index}
                  component="li"
                  variant="body2"
                  sx={{ mb: 0.75, color: "#212121", lineHeight: 1.6 }}
                >
                  {step}
                </Typography>
              ))}
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, bgcolor: "white", borderTop: "1px solid #e0e0e0", gap: 1 }}>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={onRefresh}
          sx={{
            borderColor: "#bdbdbd",
            color: "#424242",
            "&:hover": { borderColor: "#757575", bgcolor: "#f5f5f5" },
          }}
        >
          Actualizar
        </Button>
        <Button
          variant="contained"
          onClick={onClose}
          sx={{ bgcolor: "#C62828", color: "white", "&:hover": { bgcolor: "#B71C1C" }, px: 3 }}
        >
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};
